import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Tracks user activity and fires a warning + logout when idle too long.
 *
 * @param {object}   opts
 * @param {boolean}  opts.enabled       - whether to arm the timer (false when logged out)
 * @param {number}   opts.timeoutMs     - total idle time before logout
 * @param {number}   opts.warningMs     - how long the warning modal is visible before logout
 * @param {function} opts.onLogout      - called when timeout hits and user did not respond
 *
 * @returns {{showWarning: boolean, secondsLeft: number, stayLoggedIn: () => void}}
 */
export function useInactivityTimeout({
  enabled,
  timeoutMs,
  warningMs,
  onLogout,
}) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.round(warningMs / 1000));
  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const lastResetRef = useRef(Date.now());

  const clearTimers = useCallback(() => {
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    warnTimerRef.current = null;
    logoutTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  const startCountdown = useCallback(() => {
    setSecondsLeft(Math.round(warningMs / 1000));
    setShowWarning(true);
    countdownRef.current = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    logoutTimerRef.current = setTimeout(() => {
      clearTimers();
      setShowWarning(false);
      onLogout();
    }, warningMs);
  }, [warningMs, onLogout, clearTimers]);

  const resetTimers = useCallback(() => {
    // Throttle activity resets so we don't churn timers every mousemove.
    if (Date.now() - lastResetRef.current < 500 && !showWarning) return;
    lastResetRef.current = Date.now();
    clearTimers();
    setShowWarning(false);
    warnTimerRef.current = setTimeout(startCountdown, timeoutMs - warningMs);
  }, [clearTimers, startCountdown, timeoutMs, warningMs, showWarning]);

  const stayLoggedIn = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setShowWarning(false);
      return;
    }

    // Activity events we listen to.
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    const onActivity = () => {
      // While the warning is showing, ignore implicit activity — the user must
      // click "Stay logged in" explicitly so we know they're really there.
      if (showWarning) return;
      resetTimers();
    };
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    resetTimers();

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearTimers();
    };
  }, [enabled, resetTimers, clearTimers, showWarning]);

  return { showWarning, secondsLeft, stayLoggedIn };
}
